import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { activityApi } from '../../lib/api.js';
import { ActivityFeed } from '../../components/activity/ActivityFeed.js';
import { LandlordDrawer } from '../../components/landlords/LandlordDrawer.js';
import { Select } from '../../components/ui/Select.js';
import { Button } from '../../components/ui/Button.js';
import { useAuth } from '../../lib/auth.js';

export const Route = createFileRoute('/_auth/activity')({
  component: ActivityPage,
});

const TYPE_OPTIONS = [
  { value: 'call_made', label: 'Calls' },
  { value: 'whatsapp_sent', label: 'WhatsApp' },
  { value: 'note_added', label: 'Notes' },
  { value: 'stage_changed', label: 'Stage changes' },
];

const PAGE_SIZE = 50;

function ActivityPage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [typeFilter, setTypeFilter] = useState('');

  const params: Record<string, number | string | undefined> = {
    page,
    pageSize: PAGE_SIZE,
    type: typeFilter || undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['activity', params],
    queryFn: () => activityApi.list(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const events = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="px-4 pt-4 pb-3 flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-primary-text">Activity</h1>
        <div className="flex gap-2">
          <Select
            placeholder="All types"
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            aria-label="Filter by type"
            className="min-w-[130px]"
          />
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl mx-4 overflow-hidden">
        <ActivityFeed
          events={events as Parameters<typeof ActivityFeed>[0]['events']}
          isLoading={isLoading}
          showLandlord
          onLandlordClick={(id) => setSelectedId(id)}
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <p className="text-xs text-secondary">Page {page} of {totalPages}</p>
          <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}

      <LandlordDrawer landlordId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
